/**
 * Pure helpers for converting an opening's type between door / window /
 * opening.
 *
 * An opening's runtime type is its ARRAY MEMBERSHIP, not its `category` key:
 * canonical (structure) copies live in the structure doc's `doors` /
 * `windows` / `openings` arrays, and each room holds a slim
 * `{ id, structureOpeningId, parentWallIds }` copy in its own
 * `doors` / `windows` / `openings`. The renderer tags `type` from which
 * array a copy came from. So a real type change must move the canonical entry
 * AND every slim room/exterior ref between arrays in lockstep — these helpers
 * do the array moves; the orchestrator applies + persists them.
 *
 * Conversion keeps geometry (center / dimensions / rotation / parentWallIds)
 * untouched and resets `category` to the new type's defaults (correction tool,
 * not a redesign — see claude-docs/2026-05-31-opening-type-change-design.md).
 */

import { DOOR_TYPES } from '../constants/doorTypes';

export const OPENING_TYPES = ['door', 'window', 'opening'];

export const OPENING_TYPE_LABELS = {
  door: 'Door',
  window: 'Window',
  opening: 'Opening',
};

const TYPE_TO_ARRAY_KEY = {
  door: 'doors',
  window: 'windows',
  opening: 'openings',
};

/**
 * Category object for a freshly-converted opening. Old type-specific fields
 * are dropped; the new type gets a sensible default the existing sub-editors
 * can refine. Returns null for an unknown type.
 */
export const defaultCategoryForType = (toType) => {
  switch (toType) {
    case 'door':
      return { door: { doorType: DOOR_TYPES.LEFT_INSWING } };
    case 'window':
      return { window: {} };
    case 'opening':
      return { opening: {} };
    default:
      return null;
  }
};

/**
 * Resolve an opening-like object's current type from its `category` key, with
 * a fallback to the `is*` selection flags. Returns null when not an opening.
 */
export const openingTypeOf = (obj) => {
  if (!obj) return null;
  if (obj.category?.door || obj.isDoor) return 'door';
  if (obj.category?.window || obj.isWindow) return 'window';
  if (obj.category?.opening || obj.isOpening) return 'opening';
  return null;
};

// Locate the entry across the three typed arrays. Match a slim copy by
// `structureOpeningId === canonicalId` first, then any copy by
// `id === canonicalId` (the canonical itself, or a legacy fat room copy).
const findEntry = (arrays, canonicalId) => {
  for (const type of OPENING_TYPES) {
    const key = TYPE_TO_ARRAY_KEY[type];
    const list = arrays[key] || [];
    const idx = list.findIndex(
      (el) => el?.structureOpeningId === canonicalId || el?.id === canonicalId
    );
    if (idx !== -1) return { type, key, idx, entry: list[idx] };
  }
  return null;
};

// Immutably move the located entry into the `toType` array, applying
// `transform` to the moved entry. No-op (changed: false) when the entry is
// absent or already in the target array.
const moveEntry = (arrays, canonicalId, toType, transform) => {
  const found = findEntry(arrays, canonicalId);
  if (!found) return { arrays, fromType: null, changed: false };
  if (found.type === toType) {
    return { arrays, fromType: found.type, changed: false };
  }

  const toKey = TYPE_TO_ARRAY_KEY[toType];
  const out = {
    doors: [...(arrays.doors || [])],
    windows: [...(arrays.windows || [])],
    openings: [...(arrays.openings || [])],
  };
  out[found.key] = out[found.key].filter((_, i) => i !== found.idx);
  out[toKey] = [...out[toKey], transform(found.entry)];
  return { arrays: out, fromType: found.type, changed: true };
};

/**
 * Convert a CANONICAL (structure) opening: move it between the typed arrays
 * and rewrite its `category` to the new type's defaults, keeping geometry.
 *
 * @param {{doors:Array,windows:Array,openings:Array}} arrays canonical arrays
 * @param {string} canonicalId structure-opening id
 * @param {'door'|'window'|'opening'} toType
 * @returns {{arrays, fromType: string|null, changed: boolean}}
 */
export const convertCanonicalOpeningType = (arrays, canonicalId, toType) => {
  const newCategory = defaultCategoryForType(toType);
  if (!newCategory) return { arrays, fromType: null, changed: false };
  return moveEntry(arrays, canonicalId, toType, (entry) => ({
    ...entry,
    category: newCategory,
  }));
};

/**
 * Move a ROOM's slim opening ref between its typed arrays, verbatim (only
 * array membership changes — the slim ref keeps id / structureOpeningId /
 * parentWallIds). No-op when this room has no matching ref.
 *
 * @returns {{arrays, changed: boolean}}
 */
export const moveRoomCopyType = (arrays, canonicalId, toType) => {
  const { arrays: out, changed } = moveEntry(
    arrays,
    canonicalId,
    toType,
    (entry) => entry
  );
  return { arrays: out, changed };
};
